import { apiGet, apiPost } from '../../../adapters/api-client/api';
import type { EnrollmentState } from './enrollments';
import type { RubricScore } from './session-evidence';
import type { JoinPolicy, JoinWindow } from './learner';

/**
 * Learning profiles, UC-T10 learning records and the progress snapshot
 * (Sprint 3). The server owns every one of these: it decides what a profile
 * admits, versions every record, and computes the progress a family sees.
 * Nothing here derives progress from milestones.
 */

/**
 * LEM-01. The MVP offers LEP_0 and LEP_1 only (MC-01 v1-7, MC01-R13); LEP_2/3
 * are deferred. A course or enrollment with no profile is not LEP_2: the
 * server reports it as `LEGACY_UNASSIGNED`.
 */
export type LearningProfile = 'LEP_0' | 'LEP_1' | 'LEP_2' | 'LEP_3';

/** What a stored profile resolves to: a profile, or the explicit unassigned state. */
export type EffectiveLearningProfile = LearningProfile | 'LEGACY_UNASSIGNED';

/**
 * MC01-R10 — which projection a snapshot is. HISTORY_ONLY (LEP_0) and
 * SIMPLE_QUALITATIVE (LEP_1) are the MVP's; SERVICE_ONLY is an enrollment
 * whose course has no profile yet (sessions delivered, nothing about learning).
 */
export type ProgressMode =
  | 'HISTORY_ONLY'
  | 'SIMPLE_QUALITATIVE'
  | 'STRUCTURED_OUTCOME'
  | 'MASTERY_PROGRESSION'
  | 'SERVICE_ONLY';

/** LEP_1's working vocabulary — not an academic scale. */
export type ObservationState = 'STARTING' | 'DEVELOPING' | 'CONSISTENT' | 'EXTENDING';

export type FeedbackAudience = 'LEARNER_SHARED' | 'PARENT_PRIVATE' | 'TEACHER_PRIVATE';

export type LearningEvidenceStatus = 'N_A' | 'PENDING' | 'SATISFIED' | 'NOT_SATISFIED';

export type DataSufficiency = 'SUFFICIENT' | 'NOT_ENOUGH_HISTORY';

/** A teacher's latest observation, attributed (DEC-S3-P09-02). */
export interface LatestObservation {
  state: ObservationState;
  sequence: number;
  at: string;
}

export interface ProgressFeedback {
  text: string;
  at: string;
}

/**
 * One enrollment's progress snapshot, exactly as `GET /learning/progress`
 * returns it. The learner screen (UC-S11) reads the same fields minus
 * `latestParentFeedback`.
 */
export interface ProgressSnapshot {
  enrollmentId: string;
  courseId: string;
  courseTitle: string;
  learningProfile: EffectiveLearningProfile;
  /** Absent only on a snapshot from a server that predates MC-01 v1-7. */
  progressMode?: ProgressMode;
  courseVersionId?: string;
  policyVersion?: string;
  projectionVersion: number;
  asOf: string;
  enrollmentState?: EnrollmentState;
  /** Absent in SERVICE_ONLY, which says nothing about the learner. */
  sessionsAttended?: number;
  /** SERVICE_ONLY: sessions the teacher delivered. A service fact, not progress. */
  sessionsDelivered?: number;
  /**
   * Classes that took place without the learner (STU-13-FR-04). Never part of
   * `sessionsAttended` or milestones, even when the class completed for
   * payment (CHG-002). Absent when there are none.
   */
  sessionsMissed?: number;
  sessionsTotal?: number;
  nextSession?: { seq: number; scheduledStart: string; scheduledEnd?: string; bookingId?: string };
  /**
   * [start − session_prejoin_minutes, end), from the registry at read time —
   * the same rule and values the server's join check and the learner's
   * schedule use.
   */
  joinWindow?: JoinWindow;
  /** UNKNOWN until a governed mapping exists (DEC-S3-P09-02). Never a teacher's word. */
  currentState?: 'UNKNOWN';
  /** LEP_1: one teacher's note about one session, shown as that. */
  latestObservation?: LatestObservation;
  milestones?: { completed: number; total: number };
  latestLearnerFeedback?: ProgressFeedback;
  /** Guardian only. */
  latestParentFeedback?: ProgressFeedback;
  dataSufficiency: DataSufficiency;
  /** This version's next session could not be recomputed: unknown, not "none". */
  degraded?: 'NEXT_SESSION_UNRESOLVED';
}

export interface LearningProgressResponse {
  snapshots: ProgressSnapshot[];
  /** The governed values the windows were read under. */
  joinPolicy?: JoinPolicy;
  /** The server's clock; "Entrar" is decided against this, not the device's. */
  serverNow?: string;
}

export function getLearningProgress(studentId: string): Promise<LearningProgressResponse> {
  return apiGet(`/api/v1/learning/progress?studentId=${encodeURIComponent(studentId)}`);
}

/** Fields shared by every versioned learning record. */
interface VersionedRecord {
  enrollmentId: string;
  sequence: number;
  version: number;
  priorVersion?: number;
  correctionReason?: string;
  recordedAt: string;
}

export interface LearningObservation extends VersionedRecord {
  state: ObservationState;
}

export interface LearningFeedback extends VersionedRecord {
  audience: FeedbackAudience;
  text: string;
}

export interface LearningGateFact {
  sequence: number;
  type: 'session' | 'final_project';
  applicability: 'APPLICABLE' | 'NOT_APPLICABLE';
  /** Release reads delivery evidence only, in every profile (MC01-R05). */
  releaseGate?: 'DELIVERY_ONLY';
  evidenceStatus: LearningEvidenceStatus;
  deliveryStatus: LearningEvidenceStatus;
  sourceVersion?: number;
  released: boolean;
}

/** MC01-R04 — the optional in-class closing activity. A description, never a grade. */
export interface ClosingActivity extends VersionedRecord {
  summary: string;
}

/**
 * MC-02 — the optional suggested practice for one session (LEP_0, LEP_1).
 * A suggestion only: no due date, submission, state or grade exists on it.
 */
export interface SuggestedPractice extends VersionedRecord {
  text: string;
  estimatedMinutes?: number;
}

export interface TeacherLearningRecords {
  enrollmentId: string;
  learningProfile: EffectiveLearningProfile;
  progressMode?: ProgressMode;
  t10Branch?: 'NOT_REQUIRED' | 'LIGHTWEIGHT' | 'STRUCTURED' | 'FULL' | 'LEGACY_TERMS';
  admits: {
    rubric: boolean;
    observation: boolean;
    feedback: boolean;
    closingActivity?: boolean;
    suggestedPractice?: boolean;
  };
  observations: LearningObservation[];
  feedback: LearningFeedback[];
  closingActivity?: ClosingActivity[];
  suggestedPractice?: SuggestedPractice[];
  gateFacts: LearningGateFact[];
}

export function getTeacherLearningRecords(enrollmentId: string): Promise<TeacherLearningRecords> {
  return apiGet(`/api/v1/enrollments/${enrollmentId}/learning-records`);
}

/** How a write names itself: a first write omits `expectedVersion`; a correction needs it and a reason. */
export interface VersionedWrite {
  expectedVersion?: number;
  reason?: string;
  requestKey: string;
}

export function recordObservation(
  enrollmentId: string,
  sequence: number,
  body: { state: ObservationState } & VersionedWrite,
): Promise<LearningObservation> {
  return apiPost(`/api/v1/enrollments/${enrollmentId}/sessions/${sequence}/observation`, body);
}

export function publishFeedback(
  enrollmentId: string,
  sequence: number,
  body: { audience: FeedbackAudience; text: string } & VersionedWrite,
): Promise<LearningFeedback> {
  return apiPost(`/api/v1/enrollments/${enrollmentId}/sessions/${sequence}/feedback`, body);
}

/** MC-02 — record (or correct) the optional suggested practice for a session. */
export function recordSuggestedPractice(
  enrollmentId: string,
  sequence: number,
  body: { text: string; estimatedMinutes?: number } & VersionedWrite,
): Promise<SuggestedPractice> {
  return apiPost(
    `/api/v1/enrollments/${enrollmentId}/sessions/${sequence}/suggested-practice`,
    body,
  );
}

/** MC01-R04 — record (or correct) the optional closing activity. Never required. */
export function recordClosingActivity(
  enrollmentId: string,
  body: { summary: string } & VersionedWrite,
): Promise<ClosingActivity> {
  return apiPost(`/api/v1/enrollments/${enrollmentId}/closing-activity`, body);
}

export function correctRubric(
  enrollmentId: string,
  sequence: number,
  body: {
    scores: RubricScore[];
    overall: number;
    summary: string;
    asFinalRubric?: boolean;
    expectedVersion: number;
    reason: string;
    requestKey: string;
  },
): Promise<unknown> {
  return apiPost(
    `/api/v1/enrollments/${enrollmentId}/sessions/${sequence}/rubric/corrections`,
    body,
  );
}

/** A fresh idempotency key for one user action; a retry of that action reuses it. */
export function newRequestKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/* ── Past classes and family playback (UC-P10 / UC-S12) ──────────────── */

/** What the attendance record says about the learner at one class. */
export type PastClassAttendance = 'PRESENT' | 'LATE' | 'NO_SHOW' | 'UNKNOWN';

/** What the family is told about one class's recording. */
export type PastClassRecordingState =
  | 'AVAILABLE'
  | 'PROCESSING'
  /** The recording exists and has not been released to families. */
  | 'NOT_RELEASED'
  /** Withheld. The family is told it is unavailable, and not why. */
  | 'RESTRICTED'
  /** Family recording is switched off for now. */
  | 'MEDIA_UNAVAILABLE'
  | 'EXPIRED'
  | 'NOT_ENTITLED'
  | 'PLAYBACK_NOT_GRANTED'
  | 'NOT_RECORDED'
  | 'FAILED';

/** Why the server refused a playback link. */
export type RecordingRefusalReason =
  | 'NOT_OWNER'
  | 'NOT_ATTENDED'
  | 'PLAYBACK_NOT_GRANTED'
  | 'NOT_READY'
  | 'NOT_RELEASED'
  | 'RESTRICTED'
  | 'MEDIA_UNAVAILABLE'
  | 'EXPIRED'
  | 'NOT_RECORDED';

/** How one class ended, from the lifecycle (DEC-S3-P10-01). */
export type PastClassOutcome =
  | 'COMPLETED'
  | 'PARTIAL'
  | 'CANCELLED'
  | 'RESCHEDULED'
  | 'NOT_DELIVERED';

/**
 * One class the learner's session has ended for (PRT-13-FR-01/02). No money:
 * past classes are learning history, not a ledger (PRT-D06).
 */
export interface PastClass {
  enrollmentId: string;
  courseId: string;
  courseTitle: string;
  /** The course version actually taken. */
  courseVersionId?: string;
  teacherName?: string;
  seq: number;
  outcome: PastClassOutcome;
  scheduledStart?: string;
  /** Absent on a class that was never held. */
  sessionEndedAt?: string;
  attendance: PastClassAttendance;
  recording: { state: PastClassRecordingState; availableUntil?: string };
  /**
   * MC-02 — the teacher's optional suggestion to practise after this class.
   * Read-only: no due date, no submission, no state, no grade.
   */
  suggestedPractice?: { text: string; estimatedMinutes?: number; recordedAt: string };
}

/** A playback link for one click. Short-lived; never stored. */
export interface PlaybackLink {
  url: string;
  expiresAt: string;
}

/**
 * The learner's past classes, newest first.
 * GET /api/v1/learning/past-classes?studentId=
 */
export function getPastClasses(
  studentId: string,
): Promise<{ classes: PastClass[]; historyIncomplete?: boolean }> {
  return apiGet(`/api/v1/learning/past-classes?studentId=${encodeURIComponent(studentId)}`);
}

/**
 * A fresh playback link for one attended class (MVP-07). Requested on every
 * click: the link it returns expires in minutes and is bound to this learner
 * and this session.
 * POST /api/v1/learning/recordings/:enrollmentId/:seq/access
 */
export function requestRecordingAccess(
  enrollmentId: string,
  seq: number,
  studentId: string,
): Promise<PlaybackLink> {
  return apiPost(`/api/v1/learning/recordings/${encodeURIComponent(enrollmentId)}/${seq}/access`, {
    studentId,
  });
}
